import React, { useMemo, useEffect } from 'react';

import get from 'lodash/get';

import { Grid } from '@gogame/components';
import { FormField, useSiblingFieldName } from '@gogame/form';

import { AFFILIATES } from 'scenes/SmartRules/SmartRuleForm/AdvertisingPopulation';
import {
  GENERAL,
  AFFECTED_LEVEL,
  SECONDARY_AFFECTED_LEVEL,
} from 'scenes/SmartRules/SmartRuleForm/General';
import { ADVERTISING_TARGET } from 'scenes/SmartRules/SmartRuleForm/constants';
import {
  loadModifyActionOperators,
  loadModifyActions,
} from 'scenes/SmartRules/sources/fields';

import { REST_DAY_CROSS_RULE, SCHEDULE } from '../Schedule';
import {
  ACTION_TYPE,
  SKIP,
  OPERATOR,
  VALUE,
  LIMIT,
  ASK_FOR_APPROVAL,
  NONE,
} from './constants';

const normalizeValueSimple = (value) => value;

export const ModifyActionFields = ({
  name,
  value,
  hierarchyState,
  setFieldValue,
  isEdit,
}) => {
  const getSiblingFieldName = useSiblingFieldName();

  const { [ACTION_TYPE]: actionTypeId, [OPERATOR]: operatorId } = value || {};

  const { [ACTION_TYPE]: actionType, [OPERATOR]: operator } =
    get(hierarchyState, name) || {};

  const { hasSkip, hasOperator } = useMemo(() => {
    const { hasSkip, valueType } =
      actionType?.data?.find(({ id }) => id === actionTypeId) || {};

    return { hasSkip, hasOperator: valueType && valueType !== NONE };
  }, [actionTypeId, actionType]);

  const { hasLimit, valueType } = useMemo(() => {
    const { type, hasLimit } =
      operator?.data?.find(({ id }) => id === operatorId) || {};
    return { hasLimit, valueType: type?.toLowerCase() };
  }, [operatorId, operator]);

  const loadModifyActionOperatorsWithActionType = React.useCallback(
    ({ [ACTION_TYPE]: actionTypeId }, hierarchyState) => {
      const { actionType } = get(hierarchyState, name);
      const { valueType } = actionType.data.find(
        ({ id }) => id === actionTypeId
      );
      return loadModifyActionOperators({ valueType });
    },
    [name]
  );

  useEffect(() => {
    if (!isEdit) {
      setFieldValue(getSiblingFieldName(ASK_FOR_APPROVAL), true);
    }
  }, []);

  useEffect(() => {
    if (!actionTypeId) {
      setFieldValue(name, undefined);
      setFieldValue(`${SCHEDULE}.${REST_DAY_CROSS_RULE}`, undefined);
    } else {
      setFieldValue(getSiblingFieldName(ASK_FOR_APPROVAL), true);
    }
  }, [!actionTypeId]);

  const isShowApprovalCheckbox = useMemo(() => {
    return !!actionTypeId;
  }, [actionTypeId]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={4}>
        <FormField.Select
          helper={'Entities < 50 regs (last 7 days) will be skipped'}
          name={ACTION_TYPE}
          source={loadModifyActions}
          canDeselect={true}
          placeholder={'Select action'}
          depends={[
            `${GENERAL}.${AFFECTED_LEVEL}`,
            `${GENERAL}.${SECONDARY_AFFECTED_LEVEL}`,
            `${ADVERTISING_TARGET}.${AFFILIATES}`,
          ]}
          padding={'0'}
          normalizeValue={normalizeValueSimple}
          _cleanOnUnmount={true}
        />
      </Grid>
      {hasOperator ? (
        <React.Fragment>
          <Grid item xs={3}>
            <FormField.Select
              label={'Operator'}
              name={OPERATOR}
              source={loadModifyActionOperatorsWithActionType}
              depends={[getSiblingFieldName(ACTION_TYPE)]}
              lazy={true}
              _cleanOnUnmount={true}
              padding={'0'}
            />
          </Grid>
          <Grid item xs={2}>
            <FormField.TextField
              label={'Value'}
              name={VALUE}
              type={valueType}
              _cleanOnUnmount={true}
              padding={'0'}
            />
          </Grid>
          {hasLimit ? (
            <Grid item xs={2}>
              <FormField.TextField
                label={'Limit'}
                name={LIMIT}
                type={valueType}
                _cleanOnUnmount={true}
                padding={'0'}
              />
            </Grid>
          ) : null}
        </React.Fragment>
      ) : null}
      {hasSkip ? (
        <Grid item xs={12}>
          <FormField.Checkbox
            label={'Keep at least one entity active'}
            name={SKIP}
            padding={'0'}
            _cleanOnUnmount={true}
          />
        </Grid>
      ) : null}
      {isShowApprovalCheckbox ? (
        <Grid item xs={12}>
          <FormField.Checkbox
            label={'Ask for approval before performing the action'}
            name={ASK_FOR_APPROVAL}
            padding={'0'}
            _cleanOnUnmount={true}
          />
        </Grid>
      ) : null}
    </Grid>
  );
};
